import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Users, 
  Calendar, 
  Image, 
  DollarSign,
  ArrowRight,
  Clock,
  Sparkles,
  CreditCard,
  ExternalLink,
  Copy,
  TrendingUp,
  TrendingDown,
  BarChart3,
  ShoppingCart,
  Mail,
  Lock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../../App";
import DashboardNudges from "../../components/DashboardNudges";

const DashboardHome = () => {
  const { refreshUser } = useAuth();
  const [stats, setStats] = useState({
    pending_requests: 0,
    confirmed_bookings: 0,
    total_galleries: 0,
    total_purchases: 0,
    total_revenue: 0
  });
  const [subscription, setSubscription] = useState(null);
  const [photographer, setPhotographer] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [expiringGalleries, setExpiringGalleries] = useState([]);
  const [quickStats, setQuickStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  // Track if we've already handled subscription success to prevent duplicate toasts
  const [subscriptionHandled, setSubscriptionHandled] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Handle subscription success - sync subscription status from Stripe
    // Only run once per page load when subscription=success is in URL
    if (searchParams.get("subscription") === "success" && !subscriptionHandled) {
      setSubscriptionHandled(true);
      toast.success("Subscription activated successfully!");
      
      // Remove the query param to prevent re-triggering on refresh
      window.history.replaceState({}, '', '/dashboard');
      
      // Sync subscription status from Stripe directly
      const syncSubscription = async () => {
        try {
          const syncRes = await axios.post(`${API}/billing/sync-subscription`, {}, { withCredentials: true });
          
          if (syncRes.data.synced && syncRes.data.subscription_status === "active") {
            await refreshUser();
            await fetchData();
          } else {
            // Poll a few times if webhook hasn't processed yet
            let attempts = 0;
            const maxAttempts = 3;
            
            const poll = async () => {
              attempts++;
              await new Promise(resolve => setTimeout(resolve, 2000));
              
              const pollRes = await axios.post(`${API}/billing/sync-subscription`, {}, { withCredentials: true });
              
              if (pollRes.data.synced && pollRes.data.subscription_status === "active") {
                await refreshUser();
                await fetchData();
                return;
              }
              
              if (attempts < maxAttempts) {
                poll();
              } else {
                await refreshUser();
                await fetchData();
              }
            };
            
            poll();
          }
        } catch (error) {
          console.error("Error syncing subscription:", error);
          refreshUser();
        }
      };
      
      syncSubscription();
    }
    
    // Refetch data when page becomes visible (user returns to dashboard)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [searchParams, refreshUser]);

  const fetchData = async () => {
    try {
      const [statsRes, requestsRes, subRes, profileRes, expiringRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/bookings/requests`, { withCredentials: true }),
        axios.get(`${API}/billing/subscription`, { withCredentials: true }),
        axios.get(`${API}/photographer/profile`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/galleries/expiring?days=7`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setRecentRequests(requestsRes.data.slice(0, 5));
      setSubscription(subRes.data);
      setPhotographer(profileRes.data);
      setExpiringGalleries(expiringRes.data || []);
      
      // Fetch quick stats (analytics preview for all users)
      try {
        const quickStatsRes = await axios.get(`${API}/dashboard/quick-stats`, { withCredentials: true });
        setQuickStats(quickStatsRes.data);
      } catch {
        // Quick stats might not be available, that's ok
        setQuickStats(null);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRenewGallery = async (galleryId) => {
    try {
      await axios.post(`${API}/galleries/${galleryId}/renew`, {}, { withCredentials: true });
      toast.success("Gallery access extended by 30 days!");
      fetchData(); // Refresh data
    } catch (error) {
      toast.error("Failed to renew gallery access");
    }
  };

  // Generate the site link URL using the persisted slug
  const getSiteLink = () => {
    // ALWAYS use the persisted slug field from the database
    if (photographer?.slug) {
      return `${window.location.origin}/photographer/${photographer.slug}`;
    }
    // Fallback to booking link if no slug available
    return `${window.location.origin}/book/${subscription?.photographer_id || ""}`;
  };

  const statCards = [
    {
      title: "Pending Requests",
      value: stats.pending_requests,
      icon: <Clock className="w-5 h-5" />,
      color: "text-yellow-400",
      bgColor: "bg-yellow-400/10"
    },
    {
      title: "Confirmed Bookings",
      value: stats.confirmed_bookings,
      icon: <Calendar className="w-5 h-5" />,
      color: "text-green-400",
      bgColor: "bg-green-400/10"
    },
    {
      title: "Total Galleries",
      value: stats.total_galleries,
      icon: <Image className="w-5 h-5" />,
      color: "text-blue-400",
      bgColor: "bg-blue-400/10"
    },
    {
      title: "Total Revenue",
      value: `$${stats.total_revenue.toFixed(2)}`,
      icon: <DollarSign className="w-5 h-5" />,
      color: "text-primary",
      bgColor: "bg-primary/10"
    }
  ];

  const getStatusBadge = (status) => {
    const badges = {
      pending: "badge-pending",
      confirmed: "badge-confirmed",
      declined: "badge-declined",
      needs_info: "badge-needs-info"
    };
    return badges[status] || "badge-pending";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      <div>
        <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Dashboard</h1>
        <p className="text-muted-foreground">Welcome back! Here&apos;s what&apos;s happening.</p>
      </div>

      {/* Subscription Status Banner */}
      {subscription?.subscription_status === "trialing" && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-gradient-to-r from-primary/20 to-primary/5 border-primary/30">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/20 rounded-sm">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Pro Trial - {subscription.trial_days_remaining} days remaining</p>
                    <p className="text-sm text-muted-foreground">
                      You have access to all Pro features. Subscribe to keep them.
                    </p>
                  </div>
                </div>
                <Link to="/pricing">
                  <Button className="btn-primary rounded-sm" size="sm" data-testid="subscribe-cta">
                    View Plans
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Onboarding Nudges */}
      <DashboardNudges photographer={photographer} />
      
      {/* Expiring Galleries Alert */}
      {expiringGalleries.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-orange-500/10 border-orange-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-medium flex items-center gap-2 text-orange-400">
                <Clock className="w-5 h-5" />
                Galleries Expiring Soon
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {expiringGalleries.slice(0, 3).map((gallery) => (
                <div 
                  key={gallery.gallery_id} 
                  className="flex items-center justify-between p-3 bg-background/50 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{gallery.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {gallery.customer_name} • {gallery.photo_count} photos • 
                      <span className={gallery.days_until_expiry <= 2 ? "text-red-400 font-medium" : "text-orange-400"}>
                        {" "}{gallery.days_until_expiry === 0 ? "Expires today" : 
                         gallery.days_until_expiry === 1 ? "Expires tomorrow" :
                         `${gallery.days_until_expiry} days left`}
                      </span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/dashboard/galleries/${gallery.gallery_id}`}>
                      <Button variant="ghost" size="sm">
                        View
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleRenewGallery(gallery.gallery_id)}
                      className="text-orange-400 border-orange-400/30 hover:bg-orange-400/10"
                    >
                      +30 Days
                    </Button>
                  </div>
                </div>
              ))}
              {expiringGalleries.length > 3 && (
                <Link to="/dashboard/galleries?filter=expiring">
                  <Button variant="ghost" size="sm" className="w-full text-orange-400">
                    View all {expiringGalleries.length} expiring galleries
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="bg-card border-border card-hover">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-sm ${stat.bgColor}`}>
                    <span className={stat.color}>{stat.icon}</span>
                  </div>
                </div>
                <h3 className="text-3xl font-serif mb-1">{stat.value}</h3>
                <p className="text-sm text-muted-foreground">{stat.title}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Quick Stats Widget - Analytics Preview */}
      {quickStats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="bg-gradient-to-br from-card to-muted/30 border-border" data-testid="quick-stats-widget">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xl font-serif flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Quick Stats
                <Badge variant="outline" className="text-xs ml-2">Last 30 days</Badge>
              </CardTitle>
              {subscription?.current_plan?.key === "studio" ? (
                <Link to="/dashboard/analytics">
                  <Button variant="ghost" size="sm">
                    Full Analytics <ArrowRight className="ml-1 w-4 h-4" />
                  </Button>
                </Link>
              ) : (
                <Link to="/pricing">
                  <Button variant="ghost" size="sm" className="text-muted-foreground">
                    <Lock className="w-3 h-3 mr-1" /> Upgrade for Full Analytics
                  </Button>
                </Link>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Conversion Rate */}
                <div className="p-4 bg-background/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-muted-foreground">Conversion Rate</span>
                  </div>
                  <p className="text-2xl font-bold">{quickStats.conversion_rate}%</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {quickStats.confirmed_this_month} of {quickStats.requests_this_month} requests
                  </p>
                </div>

                {/* Monthly Revenue */}
                <div className="p-4 bg-background/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-primary" />
                    <span className="text-sm text-muted-foreground">Monthly Revenue</span>
                  </div>
                  <p className="text-2xl font-bold">${quickStats.revenue_this_month?.toFixed(0) || 0}</p>
                  {quickStats.revenue_change !== undefined && (
                    <p className={`text-xs mt-1 flex items-center gap-1 ${quickStats.revenue_change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {quickStats.revenue_change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {quickStats.revenue_change >= 0 ? '+' : ''}{quickStats.revenue_change}% vs last month
                    </p>
                  )}
                </div>

                {/* Download Sales */}
                <div className="p-4 bg-background/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <ShoppingCart className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-muted-foreground">Download Sales</span>
                  </div>
                  <p className="text-2xl font-bold">${quickStats.download_revenue?.toFixed(0) || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {quickStats.download_orders || 0} orders
                  </p>
                </div>

                {/* Galleries Delivered */}
                <div className="p-4 bg-background/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Image className="w-4 h-4 text-purple-500" />
                    <span className="text-sm text-muted-foreground">Galleries Delivered</span>
                  </div>
                  <p className="text-2xl font-bold">{quickStats.galleries_delivered || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {quickStats.total_photos_delivered || 0} photos
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Recent Requests */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl font-serif">Recent Requests</CardTitle>
          <Link to="/dashboard/requests">
            <Button variant="ghost" size="sm" data-testid="view-all-requests">
              View All <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentRequests.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No booking requests yet</p>
              <p className="text-sm">Share your booking link to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recentRequests.map((request) => (
                <div
                  key={request.request_id}
                  className="flex items-center justify-between p-4 bg-muted/30 rounded-sm"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{request.customer_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {request.event_type} • {request.request_types?.join(", ")}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 text-xs border rounded-sm ${getStatusBadge(request.status)}`}>
                      {request.status}
                    </span>
                    <Link to="/dashboard/requests">
                      <Button variant="ghost" size="sm">
                        View
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="bg-card border-border card-hover">
          <CardContent className="p-6">
            <Calendar className="w-8 h-8 text-primary mb-4" />
            <h3 className="text-xl font-serif mb-2">Manage Availability</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Set your available time slots and blackout dates.
            </p>
            <Link to="/dashboard/calendar">
              <Button variant="outline" className="w-full" data-testid="manage-calendar">
                Open Calendar
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="bg-card border-border card-hover">
          <CardContent className="p-6">
            <Image className="w-8 h-8 text-primary mb-4" />
            <h3 className="text-xl font-serif mb-2">Create Gallery</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Upload photos and deliver to clients.
            </p>
            <Link to="/dashboard/galleries">
              <Button variant="outline" className="w-full" data-testid="create-gallery">
                Manage Galleries
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="bg-card border-border card-hover">
          <CardContent className="p-6">
            <Users className="w-8 h-8 text-primary mb-4" />
            <h3 className="text-xl font-serif mb-2">View Requests</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Review and respond to booking requests.
            </p>
            <Link to="/dashboard/requests">
              <Button variant="outline" className="w-full" data-testid="view-requests">
                View Requests
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Your Links Section */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-xl font-serif flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-primary" />
            Your Links
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Customer Landing Page */}
          <div>
            <p className="text-sm font-medium mb-2">Customer Landing Page</p>
            <p className="text-xs text-muted-foreground mb-3">
              Share this with potential clients. It&apos;s your personalized page where they can view your portfolio, learn about you, and book sessions.
            </p>
            <div className="flex items-center gap-2">
              <Input 
                value={getSiteLink()}
                readOnly
                className="flex-1 bg-muted/30"
              />
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(getSiteLink());
                  toast.success("Landing page link copied!");
                }}
                data-testid="copy-site-link"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
          </div>

          {/* Direct Booking Link */}
          <div>
            <p className="text-sm font-medium mb-2">Direct Booking Link</p>
            <p className="text-xs text-muted-foreground mb-3">
              A direct link to your booking form. Great for sharing on social media or embedding in emails.
            </p>
            <div className="flex items-center gap-2">
              <Input 
                value={`${window.location.origin}/book/${subscription?.photographer_id || ""}`}
                readOnly
                className="flex-1 bg-muted/30"
              />
              <Button
                variant="outline"
                onClick={() => {
                  const link = `${window.location.origin}/book/${subscription?.photographer_id || ""}`;
                  navigator.clipboard.writeText(link);
                  toast.success("Booking link copied!");
                }}
                data-testid="copy-booking-link"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
          </div>

          <p className="text-xs text-muted-foreground border-t border-border pt-4">
            💡 Tip: Set your company name in Profile settings to customize your landing page URL
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardHome;
